import { EvidenceRead } from "@/services/api";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

export const splitIntoParagraphs = (text: string) => text.split(/\n+/).map((p, i) => <p key={i}>{p}</p>);

export const nth = (d: number) => {
  if (d > 3 && d < 21) return "th";
  switch (d % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

// Semi-duplicate of `downloadFile` in `file.ts` - TBD which to keep
export const download = (url: string, fileName: string) => {
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = url as string;
  link.target = "_blank";
  link.download = fileName;

  // It needs to be added to the DOM, so it can be clicked
  document.body.appendChild(link);
  link.click();

  // To make this work on Firefox we need to wait
  // a little while before removing it.
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.parentNode?.removeChild(link);
  }, 0);
};

export const downloadEvidence = (row: EvidenceRead, getLink: any) => async () => {
  const url = await getLink({
    evidenceId: row.id!,
  }).unwrap();
  download(url, row.file.file_name ?? "");
};

export function validateEmail(email: string): boolean {
  const localPartRegex = "[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+";
  const domainNameRegex = "[a-zA-Z0-9-]+";
  const domainExtensionRegex = "(?:\\.[a-zA-Z0-9-]+)+";
  const emailRegex = new RegExp(`^${localPartRegex}@${domainNameRegex}${domainExtensionRegex}$`);
  return !email || emailRegex.test(email);
}

// collator for natural sort
export const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const dataGridSortToRequestSort = (sortModel: GridSortModel): string => {
  let column = sortModel[0].field;
  if (sortModel[0].sort === "desc") {
    column = `-${column}`;
  }
  return column;
};

export const getFilterValue = (filters: GridFilterModel, name: string) =>
  filters?.items.find(f => f.field === name)?.value;

export const getFilterValues = <T = number | string>(filters: GridFilterModel, name: string): T[] =>
  filters.items.filter(f => f.field === name).map(f => f.value) as T[];

export const isValidHttpUrl = (string: string) => {
  try {
    const newUrl = new URL(string);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (_err) {
    return false;
  }
};

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

/**
 * Converts redux toolkit error to string
 * @param error
 */
export function rtkErrorToStr(error: FetchBaseQueryError | SerializedError): string {
  if ("status" in error) {
    return "error" in error ? error.error : `Failed with status code: ${error.status}`;
  }
  return error.message || `Unknown error: ${String(error)}`;
}

export function nonEmptyOrUndefined<T>(ls: T[] | undefined): T[] | undefined {
  return ls && ls.length > 0 ? ls : undefined;
}

export function queryParamsToString(
  params: Record<string, string | string[] | number | number[] | boolean | boolean[] | undefined>,
): string {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map(v => `${key}=${v}`).join("&");
      }
      return `${key}=${value}`;
    })
    .join("&");
  return queryString;
}

const AI_DISCLAIMER_PREFIX = "ai-disclaimer-accepted-";

export function hasAcceptedAIDisclaimer(language: string): boolean {
  const key = `${AI_DISCLAIMER_PREFIX}${language}`;
  return localStorage.getItem(key) === "true";
}

export function setAIDisclaimerAccepted(language: string): void {
  const key = `${AI_DISCLAIMER_PREFIX}${language}`;
  localStorage.setItem(key, "true");
}
